import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

/**
 * NOTIF.C1 — Partner Admin: per-client notification channel allowlist.
 *
 * Lets a partner admin decide which channels fire for dispatches routed
 * to this client. Backed by client_notification_config. An unset client
 * still uses the platform default (EMAIL / WEB_PUSH / IN_APP) — that's
 * represented here by the "not yet configured" banner above the toggles.
 *
 * WhatsApp is surfaced in the UI but intentionally disabled: sending via
 * WhatsApp requires a Business Account registration + template approval,
 * and we do not want partners to quietly enable a channel that will then
 * silently fall back to email.
 */

type Channel = 'EMAIL' | 'WEB_PUSH' | 'WHATSAPP' | 'IN_APP';

interface ConfigResponse {
  configured: boolean;
  channels: Channel[];
  supportedChannels: Channel[];
  updatedAt: string | null;
  updatedBy: string | null;
}

const CHANNEL_META: Record<Exclude<Channel, 'IN_APP'>, {
  label: string;
  helper: string;
  disabled: boolean;
  tooltip?: string;
}> = {
  EMAIL: {
    label: 'Email',
    helper: 'Send the default email notifications for approvals, gate failures and phase handoffs.',
    disabled: false,
  },
  WEB_PUSH: {
    label: 'Web Push',
    helper: 'Desktop push toast when the recipient has granted browser permission.',
    disabled: false,
  },
  WHATSAPP: {
    label: 'WhatsApp',
    helper: 'Urgent-only WhatsApp messages for approvals and gate failures.',
    disabled: true,
    tooltip: 'WhatsApp requires Business Account registration',
  },
};

interface Props {
  clientTenantId: string;
}

export default function ClientNotificationsTab({ clientTenantId }: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<Channel>>(new Set());
  const [initial, setInitial] = useState<Set<Channel>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setIsLoading(true);
    setError(null);
    apiFetch<ConfigResponse>(`/partner/clients/${clientTenantId}/notifications`)
      .then((res) => {
        if (!alive) return;
        const next = new Set<Channel>(res.channels);
        setSelected(next);
        setInitial(new Set(next));
        setConfigured(res.configured);
        setUpdatedAt(res.updatedAt);
      })
      .catch((err: Error) => {
        if (!alive) return;
        setError(err.message);
      })
      .finally(() => {
        if (alive) setIsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [clientTenantId]);

  const toggle = (channel: Channel) => {
    const next = new Set(selected);
    if (next.has(channel)) {
      next.delete(channel);
    } else {
      next.add(channel);
    }
    setSelected(next);
    setSaveMessage(null);
  };

  const dirty = (() => {
    if (selected.size !== initial.size) return true;
    for (const c of selected) if (!initial.has(c)) return true;
    return false;
  })();

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      const res = await apiFetch<ConfigResponse>(
        `/partner/clients/${clientTenantId}/notifications`,
        {
          method: 'PATCH',
          body: JSON.stringify({ channels: Array.from(selected) }),
        },
      );
      const next = new Set<Channel>(res.channels);
      setSelected(next);
      setInitial(new Set(next));
      setConfigured(res.configured);
      setUpdatedAt(res.updatedAt);
      setSaveMessage('Saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <p style={{ color: 'var(--text-muted)' }}>Loading notification preferences...</p>;
  }

  return (
    <div data-testid="client-notifications-tab">
      <h2 className="text-lg font-semibold mb-1" style={{ color: 'var(--text)' }}>
        Notification channels
      </h2>
      <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
        Choose which channels we use when sending notifications to this client.
        In-app bell notifications are always delivered.
      </p>

      {!configured && (
        <div
          className="rounded-lg p-3 mb-4 text-sm"
          style={{ backgroundColor: 'var(--bg-muted)', color: 'var(--text-muted)' }}
          data-testid="client-notifications-unconfigured"
        >
          Not yet configured — this client currently uses the platform default.
        </div>
      )}

      {error && (
        <div
          className="rounded-lg p-3 mb-4 text-sm"
          style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#ef4444' }}
          data-testid="client-notifications-error"
        >
          {error}
        </div>
      )}

      <div
        className="border rounded-lg divide-y"
        style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card-bg)' }}
      >
        {(Object.keys(CHANNEL_META) as Array<keyof typeof CHANNEL_META>).map((channel) => {
          const meta = CHANNEL_META[channel];
          const checked = selected.has(channel);
          const testId = `toggle-${channel.toLowerCase()}`;
          return (
            <label
              key={channel}
              className="flex items-start justify-between gap-4 p-4"
              style={{
                borderColor: 'var(--border)',
                cursor: meta.disabled ? 'not-allowed' : 'pointer',
                opacity: meta.disabled ? 0.6 : 1,
              }}
              title={meta.tooltip}
            >
              <div className="flex-1">
                <div className="font-medium" style={{ color: 'var(--text)' }}>
                  {meta.label}
                </div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                  {meta.helper}
                </div>
                {meta.disabled && meta.tooltip && (
                  <div
                    className="text-xs mt-1"
                    style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}
                    data-testid={`${testId}-tooltip`}
                  >
                    {meta.tooltip}
                  </div>
                )}
              </div>
              <input
                type="checkbox"
                checked={checked}
                disabled={meta.disabled}
                onChange={() => {
                  if (meta.disabled) return;
                  toggle(channel);
                }}
                data-testid={testId}
                aria-label={meta.label}
              />
            </label>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {updatedAt && `Last updated ${new Date(updatedAt).toLocaleString()}`}
          {saveMessage && (
            <span className="ml-2" style={{ color: '#22c55e' }} data-testid="save-confirmation">
              {saveMessage}
            </span>
          )}
        </div>
        <button
          onClick={handleSave}
          disabled={!dirty || isSaving}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{
            backgroundColor: dirty && !isSaving ? 'var(--primary)' : 'var(--border)',
            cursor: dirty && !isSaving ? 'pointer' : 'not-allowed',
          }}
          data-testid="save-notifications"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
